/**
 * First-run guidance for a freshly provisioned workshop (#1853, #1498).
 *
 * Five steps: workshop identity, opening hours, staff, lifts, service types.
 * The original three ids stay in persisted progress so a finished #1853
 * workshop is not asked to start over.
 *
 * The wizard is always skippable and always reopenable. A forced flow in a
 * new tool is how a first impression gets lost.
 *
 * Layout follows the repo's shipped wizard (`WorkOrderCreateModal`): a vertical
 * `nonLinear` Stepper with a `maxStep` high-water mark, so a visited step can
 * be reopened but an unvisited one cannot be skipped ahead to.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Step,
  StepButton,
  StepContent,
  Stepper,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { describeApiError } from '@/lib/describeApiError';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useNotify } from '@/hooks/useNotify';
import { queryKeys } from '@/hooks/queryKeys';
import { useTenantSettings } from '@/hooks/useTenantSettings';
import type { WorkshopSettings } from '@/types';
import {
  ONBOARDING_STEPS,
  isOptionalOnboardingStep,
  resumeStepIndex,
  useOnboarding,
  type OnboardingStep,
} from './useOnboarding';
import { commitWorkshopStep } from './commitWorkshopStep';
import { commitHoursStep } from './commitHoursStep';
import { WorkshopStep } from './WorkshopStep';
import { OnboardingSummary } from './OnboardingSummary';
import { HoursStep } from './HoursStep';
import { StaffStep } from './StaffStep';
import { LiftsStep } from './LiftsStep';
import { ServicesStep } from './ServicesStep';
import { toWorkshopSettings } from './toWorkshopSettings';


export default function OnboardingPage() {
  const { t } = useTranslation('onboarding');
  const { t: tc } = useTranslation('common');
  const navigate = useNavigate();
  const notify = useNotify();
  const queryClient = useQueryClient();

  const { tenantName, tenantSettings, isFetched } = useTenantSettings();
  const {
    state,
    isFetched: onboardingFetched,
    canManage,
    canEditStep,
    isComplete,
    completeStep,
    finishRemaining,
    skipAll,
    isSaving,
  } = useOnboarding();

  const [activeStep, setActiveStep] = useState(0);
  const [maxStep, setMaxStep] = useState(0);
  const [didResume, setDidResume] = useState(false);
  const [settings, setSettings] = useState<WorkshopSettings | null>(null);
  const [nameSaved, setNameSaved] = useState(false);
  const [invitedCount, setInvitedCount] = useState(0);
  const [savingWorkshop, setSavingWorkshop] = useState(false);
  const [savingHours, setSavingHours] = useState(false);
  const [showSummary, setShowSummary] = useState(false);

  // Seed the form once the settings arrive. Guarded on `settings === null` so
  // a background refetch cannot discard what the admin is currently typing.
  useEffect(() => {
    if (!isFetched || settings !== null || !tenantSettings) return;
    setSettings(toWorkshopSettings(tenantName, tenantSettings as Record<string, unknown>));
  }, [isFetched, settings, tenantName, tenantSettings]);

  // Open on the first incomplete step so a later session continues, not restarts.
  // Adjust during render: an effect would commit didResume against empty
  // progress on the fetched frame, then ignore the hydrated snapshot.
  if (onboardingFetched && !didResume) {
    const idx = resumeStepIndex(state.completedSteps);
    setActiveStep(idx);
    setMaxStep(idx);
    setDidResume(true);
  }

  const busy = savingWorkshop || savingHours || isSaving;
  const busyRef = useRef(busy);
  busyRef.current = busy;

  const goTo = useCallback((index: number) => {
    setActiveStep(index);
    setMaxStep((prev) => Math.max(prev, index));
  }, []);

  const goToIfIdle = useCallback((index: number) => {
    if (busyRef.current) return;
    goTo(index);
  }, [goTo]);

  const recordProgress = useCallback(
    async (step: OnboardingStep) => {
      try {
        await completeStep(step);
      } catch (error) {
        notify.error(tc('error'), describeApiError(error, tc, { fallback: t('saveFailed') }));
      }
    },
    [completeStep, notify, t, tc],
  );

  const handleWorkshopNext = async () => {
    if (!settings || busyRef.current) return;
    setSavingWorkshop(true);
    try {
      const saved = await commitWorkshopStep({
        settings,
        nameAlreadySaved: nameSaved,
        onNameSaved: () => setNameSaved(true),
      });
      // The API recomputes the net rate from the gross one; keep its answer.
      setSettings((prev) =>
        prev
          ? {
              ...prev,
              laborRateCents: saved.laborRateCents ?? prev.laborRateCents,
              laborRateGrossCents: saved.laborRateGrossCents,
            }
          : prev,
      );
      await queryClient.invalidateQueries({ queryKey: queryKeys.tenant.all });
      await completeStep('workshop');
      notify.success(t('saved'), t('workshop.savedDesc'));
      goTo(activeStep + 1);
    } catch (error) {
      notify.error(tc('error'), describeApiError(error, tc, { fallback: t('saveFailed') }));
    } finally {
      setSavingWorkshop(false);
    }
  };

  const handleHoursNext = async () => {
    if (!settings || busyRef.current) return;
    setSavingHours(true);
    try {
      await commitHoursStep(settings.openingHours);
      await queryClient.invalidateQueries({ queryKey: queryKeys.tenant.all });
      await completeStep('hours');
      notify.success(t('saved'), t('hours.savedDesc'));
      goTo(activeStep + 1);
    } catch (error) {
      notify.error(tc('error'), describeApiError(error, tc, { fallback: t('saveFailed') }));
    } finally {
      setSavingHours(false);
    }
  };

  const handleSkipStep = async (index: number) => {
    if (busyRef.current) return;
    const step = ONBOARDING_STEPS[index];
    if (!step || !isOptionalOnboardingStep(step)) return;
    try {
      await completeStep(step);
      if (index >= ONBOARDING_STEPS.length - 1) {
        setShowSummary(true);
        return;
      }
      goTo(index + 1);
    } catch (error) {
      notify.error(tc('error'), describeApiError(error, tc, { fallback: t('saveFailed') }));
    }
  };

  /**
   * Finishing shows what is still missing rather than dropping the workshop on
   * the dashboard with a toast (#2490). Skipping stays a plain exit: that is a
   * deliberate "not now", not a claim of being done.
   */
  const handleFinish = async () => {
    if (busyRef.current) return;
    try {
      await finishRemaining();
      setShowSummary(true);
    } catch (error) {
      notify.error(tc('error'), describeApiError(error, tc, { fallback: t('saveFailed') }));
    }
  };

  const handleSkipAll = async () => {
    if (busyRef.current) return;
    try {
      await skipAll();
      void navigate('/', { replace: true });
    } catch (error) {
      notify.error(tc('error'), describeApiError(error, tc, { fallback: t('saveFailed') }));
    }
  };

  const isStepDone = (step: OnboardingStep) => state.completedSteps.includes(step);

  if (!isFetched || !settings || !didResume) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Saving progress needs the workshop-settings permission; without it the
  // wizard would collect input the API refuses to store.
  if (!canManage) {
    return (
      <Container maxWidth="sm" sx={{ py: 6 }}>
        <Alert severity="info">{t('adminOnly')}</Alert>
      </Container>
    );
  }

  const stepBody = (step: OnboardingStep) => {
    switch (step) {
      case 'workshop':
        return <WorkshopStep settings={settings} onChange={setSettings} />;
      case 'hours':
        return (
          <HoursStep
            hours={settings.openingHours}
            onChange={(openingHours) => setSettings({ ...settings, openingHours })}
          />
        );
      case 'staff':
        return (
          <StaffStep
            canManage={canEditStep('staff')}
            invitedCount={invitedCount}
            onInvited={() => {
              setInvitedCount((n) => n + 1);
              void recordProgress('staff');
            }}
          />
        );
      case 'lifts':
        return (
          <LiftsStep
            canManage={canEditStep('lifts')}
            onLiftCountChange={(count) => {
              if (count > 0) void recordProgress('lifts');
            }}
          />
        );
      case 'services':
        return (
          <ServicesStep
            canManage={canEditStep('services')}
            onServiceCountChange={(count) => {
              if (count > 0) void recordProgress('services');
            }}
          />
        );
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h5" gutterBottom>
        {t('title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('subtitle')}
      </Typography>

      {isComplete && !showSummary && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {t('alreadyComplete')}
        </Alert>
      )}

      {showSummary && (
        <OnboardingSummary
          settings={settings}
          onDone={() => void navigate('/', { replace: true })}
        />
      )}

      {!showSummary && (
      <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider' }}>
        <Stepper activeStep={activeStep} orientation="vertical" nonLinear>
          {ONBOARDING_STEPS.map((step, index) => {
            const isLast = index === ONBOARDING_STEPS.length - 1;
            return (
              <Step key={step} completed={isStepDone(step)}>
                <StepButton
                  onClick={() => { if (index <= maxStep) goToIfIdle(index); }}
                  disabled={index > maxStep || busy}
                  optional={
                    isOptionalOnboardingStep(step) ? (
                      <Typography variant="caption" color="text.secondary">
                        {t('optional')}
                      </Typography>
                    ) : undefined
                  }
                >
                  {t(`${step}.title`)}
                </StepButton>

                <StepContent TransitionProps={{ unmountOnExit: true }}>
                  <Box sx={{ pt: 1 }}>{stepBody(step)}</Box>

                  <Box sx={{ display: 'flex', gap: 1, mt: 3, flexWrap: 'wrap' }}>
                    {index > 0 && (
                      <Button color="inherit" onClick={() => goToIfIdle(index - 1)} disabled={busy}>
                        {tc('previous')}
                      </Button>
                    )}

                    {step === 'workshop' ? (
                      <Button
                        variant="contained"
                        onClick={() => void handleWorkshopNext()}
                        disabled={busy || !settings.name}
                      >
                        {savingWorkshop && <CircularProgress size={20} sx={{ mr: 1 }} />}
                        {tc('save')}
                      </Button>
                    ) : step === 'hours' ? (
                      <>
                        <Button
                          variant="contained"
                          onClick={() => void handleHoursNext()}
                          disabled={busy}
                        >
                          {savingHours && <CircularProgress size={20} sx={{ mr: 1 }} />}
                          {tc('save')}
                        </Button>
                        <Button color="inherit" onClick={() => void handleSkipStep(index)} disabled={busy}>
                          {t('skipStep')}
                        </Button>
                      </>
                    ) : !isLast ? (
                      <>
                        <Button variant="contained" onClick={() => goToIfIdle(index + 1)} disabled={busy}>
                          {tc('next')}
                        </Button>
                        <Button color="inherit" onClick={() => void handleSkipStep(index)} disabled={busy}>
                          {t('skipStep')}
                        </Button>
                      </>
                    ) : (
                      <Button
                        variant="contained"
                        onClick={() => void handleFinish()}
                        disabled={busy}
                      >
                        {isSaving && <CircularProgress size={20} sx={{ mr: 1 }} />}
                        {t('finish')}
                      </Button>
                    )}
                  </Box>
                </StepContent>
              </Step>
            );
          })}
        </Stepper>
      </Paper>
      )}

      {!showSummary && (
        <Box sx={{ mt: 3 }}>
          <Button color="inherit" onClick={() => void handleSkipAll()} disabled={busy}>
            {t('skipAll')}
          </Button>
        </Box>
      )}
    </Container>
  );
}
